import { Router } from '@angular/router';
import { NetworkRequiredPage } from './public/network-required/network-required.page';
import {
  selectBaseDataState,
  selectNativeUploadsEnabled,
} from './state/base-data/base-data.selectors';
import { loadAllBaseData } from './state/base-data/base-data.actions';
import { Component, ChangeDetectorRef, NgZone, ViewChild } from '@angular/core';

import { IonLoading, ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '@services/auth/authentication.service';
import { Store, select } from '@ngrx/store';
import { AppState } from './state';

import { UpdateStatus } from '@shared/components/update-status/update-status.component';
import { VersionService } from '@services/version/version.service';
import { selectMultipleTimeCards } from './state/multiple-timecards/multiple-timecards.selectors';
import { MultipleTimecardsModalComponent } from '@shared/components/multiple-timecards-modal/multiple-timecards-modal.component';
import { UserTimecard } from './models';
import { selectMultipleTimeCardJobs } from './state/multiple-timecards/multiple-timecards.join-selectors';
import { take } from 'rxjs/operators';
import { UserMeService } from '@services/api/user-me.service';
import { appBackground, appResume } from './state/app-state/app-state.actions';
import {
  uploadProgressed,
  uploadQueueSynced,
  uploadStateChanged,
} from './state/upload-queue/upload-queue.actions';
import { UploadQueueService } from '@services/upload/upload-queue.service';
import { clearLegacyUploadQueue } from '@services/upload/legacy-cleanup';
import { StorageService } from '@services/storage/storage.service';

import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { Device, DeviceInfo } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { ServerStatusWsService } from '@services/ws/server-status-ws/server-status-ws.service';
import { MaintenanceModePage } from '@public/maintenance-mode/maintenance-mode.page';
import { DeviceAPNS } from './models/device-apns.model';
import { PushRegistrationService } from '@services/api/push-registration.service';
import { PluginListenerHandle } from '@capacitor/core';
import { S3UploadService } from '@services/api/s3-upload.service';
import { register } from 'swiper/element/bundle';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { IonModal } from '@ionic/angular/common';
import { SignatureService } from '@services/misc/signature.service';

interface PushNotificationListeners {
  registration: PluginListenerHandle;
  registrationError: PluginListenerHandle;
  notificactionReceived: PluginListenerHandle;
  actionPerformed: PluginListenerHandle;
}

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  @ViewChild('updateModal') modal!: IonModal;
  @ViewChild('loading') loading!: IonLoading;
  subs = new Subscription();
  loaded = false;
  authenticated = false;
  firstLogin = false;
  networkHandler: any;
  isConnected = true;
  maintenanceMode = false;
  netReqPage = NetworkRequiredPage;
  maintenancePage = MaintenanceModePage;
  deviceInfo: DeviceInfo;
  deviceId: string;

  updateAvailable = false;
  updateStatus: UpdateStatus;

  uploading = false;

  pushListeners: PushNotificationListeners;
  updateDownloadProgress = 0.0;

  constructor(
    private platform: Platform,
    private authService: AuthenticationService,
    private store$: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private version: VersionService,
    private modalCtrl: ModalController,
    private userMeSvc: UserMeService,
    public serverStatus: ServerStatusWsService,
    private pushRegistration: PushRegistrationService,
    private zone: NgZone,
    private uploadSvc: S3UploadService,
    public signatureService: SignatureService,
    private uploadQueue: UploadQueueService,
    private storage: StorageService
  ) {
    this.initializeApp();
    LiveUpdate.ready()
      .then(() => {})
      .catch(err => {});
  }

  initializeApp() {
    this.pushListeners = {
      registration: null,
      registrationError: null,
      notificactionReceived: null,
      actionPerformed: null,
    };
    App.addListener('appStateChange', async state => {
      if (!state.isActive) {
        // The app has become inactive. We should check if we have some work left to do, and, if so,
        // execute a background task that will allow us to finish that work before the OS
        // suspends or terminates our app:
        if (this.uploading) {
          this.store$.dispatch(appBackground());
        }
      } else {
        this.store$.dispatch(appResume());
        if (this.authenticated) {
          try {
            await this.checkForUpdates();
            await this.addPushNotificationListeners();
            await this.registerPushNotifications();
          } catch (err) {
            console.log(err);
          }
          this.loaded = false;
          this.store$.dispatch(loadAllBaseData());
          const path = this.router.url.split('/');
          const skipReloadEndpoints = [
            'addl-cost',
            'info',
            'attachments',
            'daily',
          ];
          const skip =
            path.includes('jobs') && skipReloadEndpoints.includes(path.pop());
          if (this.deviceInfo.platform !== 'web' && !skip) {
            this.zone.run(() => {
              this.router.navigateByUrl('/');
            });
          } else if (skip) {
            console.log('---- Skipping Reload ----');
          }
        }
      }
    });

    this.platform.ready().then(() => {
      this.networkHandler = Network.addListener(
        'networkStatusChange',
        status => {
          this.isConnected = status.connected;
          this.cdr.detectChanges();
        }
      );
      StatusBar.show().catch(e => {});
      SplashScreen.hide().catch(e => {});
      this.subs.add(
        this.uploadSvc.activeUploads$.subscribe(uploading => {
          this.uploading = uploading;
        })
      );
      this.startUploadQueue();
      this.subs.add(
        this.store$.pipe(select(selectMultipleTimeCards)).subscribe(mtc => {
          if (mtc.timecards.length > 0) {
            this.multipleTimecardsModal(mtc.timecards);
          }
        })
      );
      this.subs.add(
        this.store$.pipe(select(selectBaseDataState)).subscribe(bds => {
          this.loaded = bds.loaded;
          if (this.loaded) {
            this.router.initialNavigation();
          }
        })
      );
      // Server-controlled kill switch for the native uploader. Re-read on
      // every base-data load -- which includes each app resume -- so flipping
      // the constance flag reaches the field without an App Store release or
      // even an OTA bundle.
      this.subs.add(
        this.store$
          .pipe(select(selectNativeUploadsEnabled))
          .subscribe(enabled => this.uploadQueue.setEnabled(enabled))
      );
      this.subs.add(
        this.authService.authenticationState.subscribe(async authState => {
          if (authState.authenticated && authState.token) {
            this.deviceInfo = await Device.getInfo();
            this.deviceId = (await Device.getId()).identifier;
            this.authenticated = true;
            try {
              await this.checkForUpdates();
              await this.addPushNotificationListeners();
              await this.registerPushNotifications();
            } catch (error) {
              console.log(
                'PushUpdate does not have web implementation, and isnt loaded.'
              );
            }
            this.store$.dispatch(loadAllBaseData());
            this.serverStatus.setupWebSocket();
            this.serverStatus.maintenance$.subscribe(maintenance => {
              this.maintenanceMode = maintenance;
            });
          } else {
            this.router.initialNavigation();
          }
        })
      );
      if (!this.platform.is('capacitor')) {
        this.version.initVersionCheck();
      }
    });
    this.platform.resume.subscribe(async () => {});
  }

  /**
   * Arms the native upload queue and bridges it into the store.
   *
   * The service's `queueSynced$` is the authoritative view of the native
   * queue -- `sync()`, `enqueue()`, `retry()`, `cancel()`, `setAuth()` and
   * `clearAuth()` all push through it, as does the effect that re-syncs on
   * resume. So one subscription here keeps the slice in step with every one
   * of them, rather than each call site having to remember to dispatch.
   *
   * Subscribed unfiltered and on purpose. It is `queue$`, not this stream,
   * that re-emits per progress chunk; a native re-read carries `bytesSent`,
   * `attempts` and `lastError` that arrive by no other route, and dropping a
   * resume resync because the item's state happened not to change is exactly
   * how a progress bar gets stuck.
   *
   * Started unconditionally at platform ready rather than on login: uploads
   * survive app termination, so a queue left over from a previous launch has
   * to be visible before anyone signs in again.
   *
   * Every dispatch below runs inside `this.zone.run`, the same re-entry the
   * `appStateChange` listener above needs and for the same reason: these all
   * originate in Capacitor plugin listeners, which native invokes outside
   * Angular's zone. Doing it here rather than in each subscriber means one
   * logical event costs one change-detection pass instead of one per
   * selector, and -- more to the point -- it covers subscribers that have no
   * zone plumbing of their own, notably the `async` pipe in
   * `UploadDetailSheetComponent`, which `ModalController` creates long after
   * this runs.
   */
  private startUploadQueue() {
    this.subs.add(
      this.uploadQueue.queueSynced$.subscribe(items => {
        this.zone.run(() => {
          this.store$.dispatch(uploadQueueSynced({ items }));
        });
      })
    );
    this.uploadQueue
      .start(
        event =>
          this.zone.run(() =>
            this.store$.dispatch(
              uploadProgressed({
                id: event.id,
                bytesSent: event.bytesSent,
                bytesTotal: event.bytesTotal,
              })
            )
          ),
        event =>
          this.zone.run(() =>
            this.store$.dispatch(
              uploadStateChanged({
                id: event.id,
                entityId: event.entityId,
                entityType: event.entityType,
                state: event.state,
                error: event.error,
              })
            )
          )
      )
      .catch(err => {
        console.log('Upload queue failed to start: ', err);
      });

    // One-time discard of the pre-native upload queue. Deliberately not
    // chained onto start(): it only touches Storage and Filesystem and shares
    // no state with the native queue, so a queue that fails to start must
    // still not leave the old entries sitting there for the next launch.
    clearLegacyUploadQueue(this.storage).catch(err => {
      console.log('Legacy upload cleanup failed: ', err);
    });
  }

  async multipleTimecardsModal(timecards: UserTimecard[]) {
    const jobs = await this.store$
      .pipe(
        select(selectMultipleTimeCardJobs),
        take(1)
      )
      .toPromise();
    const modal = await this.modalCtrl.create({
      component: MultipleTimecardsModalComponent,
      componentProps: {
        timecards,
        jobs,
      },
      backdropDismiss: false,
    });

    await modal.present();

    const data = await modal.onDidDismiss();
    if (data && data.data) {
      this.userMeSvc.deleteTimecard(data.data).subscribe(async res => {
        location.reload();
      });
    }
  }

  async clearPushNotificationListeners() {
    Object.values(this.pushListeners).forEach(
      async (listener: PluginListenerHandle) => {
        if (listener) {
          await listener.remove();
        }
      }
    );
  }

  async addPushNotificationListeners() {
    await this.clearPushNotificationListeners();
    this.pushListeners.registration = await PushNotifications.addListener(
      'registration',
      token => {
        console.info(
          'Registration token Receieved: ',
          token.value.slice(0, 10)
        );
        console.info('Device Details', this.deviceId);
        const payload: DeviceAPNS = {
          name: this.deviceInfo.name ? this.deviceInfo.name : '',
          registrationId: token.value,
          deviceId: this.deviceId.toLowerCase(),
          active: true,
        };
        this.pushRegistration
          .registerAPNS(payload)
          .pipe(take(1))
          .subscribe(resp => {
            console.log('Succesfully registered device with server.');
          });
      }
    );

    this.pushListeners.registrationError = await PushNotifications.addListener(
      'registrationError',
      err => {
        console.error('Registration error: ', err.error);
      }
    );

    this.pushListeners.notificactionReceived = await PushNotifications.addListener(
      'pushNotificationReceived',
      notification => {
        console.log('Push notification received: ', notification);
      }
    );

    this.pushListeners.actionPerformed = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      notification => {
        console.log(
          'Push notification action performed',
          notification.actionId,
          notification.inputValue
        );
      }
    );
  }

  async registerPushNotifications() {
    console.log('Registering for push notifications');
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      await this.userMeSvc.setPushStatus(false).toPromise();
      throw new Error('User denied permissions!');
    }
    await this.userMeSvc.setPushStatus(true).toPromise();
    await PushNotifications.register();
  }

  async checkForUpdates() {
    const buildNumber = (await App.getInfo()).build;
    const result = await LiveUpdate.fetchLatestBundle({
      channel: `vr-build-${buildNumber}`,
    });
    const currentBundle = await LiveUpdate.getCurrentBundle();
    if (!result.bundleId || result.bundleId == currentBundle.bundleId) {
      return;
    }
    await this.loading.present();
    await LiveUpdate.downloadBundle({
      url: result.downloadUrl,
      bundleId: result.bundleId,
    });
    await LiveUpdate.setNextBundle({
      bundleId: result.bundleId,
    });
    await this.loading.dismiss();
    await LiveUpdate.reload();
  }
}
